/** Business KPI card component for displaying metrics with trend indicators. */
import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Skeleton,
  SxProps,
  Theme,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from "@mui/icons-material";

export type CardColor = "primary" | "success" | "info" | "warning" | "error";

interface BusinessKPICardProps {
  title: string;
  value: string | number;
  trend?: string; // "+12%" or "-5%"
  period?: string; // "Today", "This Month"
  icon?: React.ReactNode;
  color?: CardColor;
  loading?: boolean;
  onClick?: () => void; // For expand/detail view
}

/** Get trend icon and color based on trend value. */
const getTrendConfig = (trend: string): {
  icon: React.ReactNode;
  color: string;
} => {
  const numTrend = parseFloat(trend);
  if (numTrend > 0) {
    return { icon: <TrendingUp fontSize="small" />, color: "success.main" };
  } else if (numTrend < 0) {
    return { icon: <TrendingDown fontSize="small" />, color: "error.main" };
  } else {
    return { icon: <TrendingFlat fontSize="small" />, color: "text.secondary" };
  }
};

/** Get color mapping for MUI theme. */
const getColorValue = (color: CardColor): string => {
  const colorMap: Record<CardColor, string> = {
    primary: "primary.main",
    success: "success.main",
    info: "info.main",
    warning: "warning.main",
    error: "error.main",
  };
  return colorMap[color] || "primary.main";
};

/** Business KPI card displaying title, value, trend, and period. */
export default function BusinessKPICard({
  title,
  value,
  trend,
  period,
  icon,
  color = "primary",
  loading = false,
  onClick,
}: BusinessKPICardProps) {
  const colorValue = getColorValue(color);
  const isClickable = !!onClick;

  if (loading) {
    return (
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          cursor: isClickable ? "pointer" : "default",
        }}
        elevation={2}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box>
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" height={48} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: isClickable ? "pointer" : "default",
        transition: "transform 0.2s, box-shadow 0.2s",
        borderTop: `4px solid ${colorValue}`,
        "&:hover": isClickable
          ? {
              transform: "translateY(-2px)",
              boxShadow: 4,
            }
          : {},
      }}
      elevation={2}
      onClick={onClick}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box>
          {/* Title */}
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{
              fontWeight: 500,
              textTransform: "uppercase",
              fontSize: "0.75rem",
            }}
          >
            {title}
          </Typography>

          {/* Value */}
          <Typography
            variant="h4"
            component="div"
            sx={{ fontWeight: "bold", color: colorValue, mb: 1 }}
          >
            {value}
          </Typography>

          {/* Trend Row */}
          {(trend || period) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {trend && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {getTrendConfig(trend).icon}
                  <Typography
                    variant="body2"
                    sx={{ color: getTrendConfig(trend).color, fontWeight: 500 }}
                  >
                    {trend}
                  </Typography>
                </Box>
              )}
              {period && (
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: "action.hover",
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    color: "text.secondary",
                  }}
                >
                  {period}
                </Typography>
              )}
            </Box>
          )}

          {/* Optional icon at bottom right */}
          {icon && (
            <Box
              sx={{
                position: "absolute",
                bottom: 16,
                right: 16,
                opacity: 0.1,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
